import i18n from '@/utils/i18n';
import React from 'react';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { Button } from './button';
import { GET_INVOICE_DETAIL_QUERY } from '@/screens/invoice-detail/invoice-detail-query';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT } from './orders/hooks/use-get-order';
import {
  OrderInvoiceCheckInFieldsFragment,
  Start_CheckInMutation,
  Start_CheckInMutationVariables,
} from '@/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { useNavigation } from '@react-navigation/native';

const START_CHECK_IN_MUTATION = gql`
  mutation Start_CheckIn($invoiceUUID: uuid!) {
    retrieveOrderInvoiceCheckInItems(order_invoice_id: $invoiceUUID) {
      order_invoice_check_in {
        ...OrderInvoiceCheckInFields
      }
    }
  }

  ${ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT}
`;

interface Props {
  existingCheckIn?: OrderInvoiceCheckInFieldsFragment | null;
  invoiceUUID: string;
  orderUUID?: string;
  trackingData: TrackingData | null;
}

export const CheckInButton: React.FC<Props> = ({
  existingCheckIn,
  invoiceUUID,
  orderUUID,
  trackingData,
}) => {
  let checkInLabel = i18n.t('check_in.button.resume');
  if (!existingCheckIn) {
    checkInLabel = i18n.t('check_in.button.start');
  } else if (existingCheckIn?.completed_at) {
    checkInLabel = i18n.t('check_in.button.edit');
  }
  const navigation =
    useNavigation<StackNavigationProp<AppContainerStackParams>>();

  const [startCheckIn, { loading }] = useMutation<
    Start_CheckInMutation,
    Start_CheckInMutationVariables
  >(START_CHECK_IN_MUTATION, {
    refetchQueries: [getOperationName(GET_INVOICE_DETAIL_QUERY) as string],
    variables: {
      invoiceUUID,
    },
  });

  const onButtonPress = async () => {
    let checkInUUID;
    if (!existingCheckIn) {
      const { data } = await startCheckIn();
      checkInUUID = data?.retrieveOrderInvoiceCheckInItems
        .order_invoice_check_in?.uuid as string;
    } else {
      checkInUUID = existingCheckIn.uuid;
    }
    navigation.navigate(APP_CONTAINER_ROUTES.OrderCheckIn, {
      checkInCompleted: Boolean(existingCheckIn?.completed_at),
      checkInUUID,
      invoiceUUID,
      orderUUID,
    });
  };

  return (
    <Button
      loading={loading}
      onPress={onButtonPress}
      trackingData={trackingData}
    >
      {checkInLabel}
    </Button>
  );
};
